import { useCallback, useEffect, useRef, useState } from "react";

import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { debounce } from "lodash";
import { useLocation, useSearchParams } from "react-router-dom";
import { haveQueryParamsChanged } from "utils";
import { ONE, SEARCH_DEBOUNCE_TIMER, ZERO } from "utils/constants";

import { DEFAULT_PAGINATION_LIMIT, PAGE_ONE } from "ui/pagination";

import { useUpdateQueryParams } from "./useUpdateQueryParams";

export interface IUseTableActionsArgs {
  isLoadingTable: boolean;
  refetch?: (
    options?: RefetchOptions
  ) => Promise<QueryObserverResult<unknown, Error>>; // make compulsory when all tables have been refactored to use react-query
  onTableRefresh?: () => void;
}

export enum ResetFilterEnum {
  TABLE = "normal",
  PAGE = "true",
}

export const useTableActions = (props: IUseTableActionsArgs) => {
  const { isLoadingTable, refetch, onTableRefresh } = props;
  const tableRefreshCount = useRef(ZERO);
  const updateQueryParams = useUpdateQueryParams();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const search = searchParams.get("search");
  const [searchQuery, setSearchQuery] = useState(search || "");

  const handleRefresh = () => {
    setIsRefreshing(false);
    if (!refetch) {
      return;
    }
    refetch();
  };

  const handleRefreshTable = async () => {
    setIsRefreshing(true);
    setSearchQuery("");
    tableRefreshCount.current += ONE;

    const newQueryParams: Record<string, string | null> = {
      isDateFilter: null,
      resetFilter: ResetFilterEnum.TABLE,
    };

    if ((refetch && tableRefreshCount.current === ONE) || !refetch) {
      newQueryParams["tableRefreshCount"] =
        tableRefreshCount.current.toString();
    }

    if (
      haveQueryParamsChanged({
        currentSearch: location.search,
        newParams: newQueryParams,
      })
    ) {
      updateQueryParams(newQueryParams);
    } else {
      handleRefresh();
    }
    // when you've fully refactored to use only react-query, remove this lines below
    const isDateFilter = searchParams.get("isDateFilter");
    if (isDateFilter && onTableRefresh) {
      onTableRefresh();
    }
  };

  /**
   * Performs table search by updating the url params and resetting the ones not needed like: isDateFilter, tableRefreshCount and resetFilter.
   * Should only be used on tables using react-query to fetch data
   * @param {string} query Search query
   * @returns {void} No return data
   */
  const onTableSearch = useCallback(
    debounce(async (query: string) => {
      if (query.trim() === "") {
        updateQueryParams({
          ...Object.fromEntries(new URLSearchParams(location.search)),
          page: String(PAGE_ONE),
          limit: String(DEFAULT_PAGINATION_LIMIT),
          search: null,
          isDateFilter: null,
          tableRefreshCount: null,
          resetFilter: null,
        });
        return;
      }

      updateQueryParams({
        ...Object.fromEntries(new URLSearchParams(location.search)),
        page: String(PAGE_ONE),
        limit: String(DEFAULT_PAGINATION_LIMIT),
        search: query,
        isDateFilter: null,
        tableRefreshCount: null,
        resetFilter: null,
      });
    }, SEARCH_DEBOUNCE_TIMER),
    [location]
  );

  /* Should only be used on tables using react-query to fetch data */
  const handleSearchQueryChange = (
    evt: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = evt.target;
    setSearchQuery(value);
    onTableSearch(value);
  };

  useEffect(() => {
    if (!isLoadingTable && isRefreshing) {
      setIsRefreshing(false);
    }
  }, [isLoadingTable]);

  return {
    handleRefreshTable,
    onTableSearch,
    handleSearchQueryChange,
    isRefreshing,
    setIsRefreshing,
    searchQuery,
  };
};
